import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit2, Loader2, ArrowLeft, Tag, GripVertical,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminCategoriesPage = () => {
  const { api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCat, setEditCat] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    try {
      const res = await api.get('/categories/admin/all');
      setCategories(res.data);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editCat.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editCat._isNew) {
        const res = await api.post('/categories', editCat);
        setCategories(prev => [...prev, res.data]);
        toast.success('Category created');
      } else {
        const res = await api.put(`/categories/${editCat.id}`, editCat);
        setCategories(prev => prev.map(c => c.id === editCat.id ? res.data : c));
        toast.success('Category updated');
      }
      setEditCat(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? (Only possible when no course uses it.)')) return;
    try {
      await api.delete(`/categories/${id}`);
      setCategories(prev => prev.filter(c => c.id !== id));
      toast.success('Category deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-categories-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1" /> Admin</Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#2a3e5d]">Course Categories</h1>
              <p className="text-sm text-slate-500">{categories.length} categories</p>
            </div>
          </div>
          <Button onClick={() => setEditCat({ _isNew: true, name: '', description: '' })} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="add-category-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Category
          </Button>
        </div>

        <div className="space-y-2">
          {categories.map((cat, i) => (
            <div
              key={cat.id}
              className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 px-4 py-3 hover:border-slate-300 transition-colors"
              data-testid={`category-${cat.id}`}
            >
              <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
              <Tag className="h-4 w-4 text-[#20acf6] shrink-0" />
              <div className="flex-1">
                <span className="font-medium text-sm text-[#2a3e5d]">{cat.name}</span>
                <span className="ml-2 text-xs text-slate-400" data-testid={`category-count-${cat.id}`}>{cat.course_count ?? 0} course{cat.course_count === 1 ? '' : 's'}{cat.status === 'hidden' ? ' · hidden' : ''}</span>
                {cat.description && <p className="text-xs text-slate-400 mt-0.5">{cat.description}</p>}
              </div>
              <Badge variant="outline" className="text-[10px]">#{i + 1}</Badge>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditCat(cat)} data-testid={`edit-cat-${cat.id}`}>
                <Edit2 className="h-3.5 w-3.5 text-slate-500" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => handleDelete(cat.id)} data-testid={`delete-cat-${cat.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          {categories.length === 0 && (
            <Card><CardContent className="py-12 text-center">
              <Tag className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No categories yet. Add your first one.</p>
            </CardContent></Card>
          )}
        </div>
      </div>

      <Dialog open={!!editCat} onOpenChange={() => setEditCat(null)}>
        <DialogContent className="sm:max-w-md" data-testid="category-edit-modal">
          <DialogHeader>
            <DialogTitle className="text-[#2a3e5d]">{editCat?._isNew ? 'Add Category' : 'Edit Category'}</DialogTitle>
          </DialogHeader>
          {editCat && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Category Name *</Label>
                <Input value={editCat.name} onChange={(e) => setEditCat(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Safeguarding" data-testid="category-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Description (optional)</Label>
                <Input value={editCat.description || ''} onChange={(e) => setEditCat(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of this category" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saving} className="bg-[#20acf6] hover:bg-[#3bb8f7] flex-1" data-testid="save-category-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {saving ? 'Saving...' : 'Save Category'}
                </Button>
                <Button variant="outline" onClick={() => setEditCat(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCategoriesPage;
